import {
  Button,
  Checkbox,
  NumberInput,
  Select,
  TagsInput,
  Textarea,
  TextInput,
} from '@mantine/core';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { NewIntegrationBody, UpdateIntegrationBody } from '@/api/integrations';
import {
  adapterFieldCopy,
  adapterName,
  adapterTitleHint,
  isFieldShown,
  validationMessage,
} from '@/components/Integrations/adapters/copy';
import { getAdapterMeta } from '@/components/Integrations/adapters/registry';
import {
  capabilitiesFor,
  deliveryFacts,
  useIntegrationCapabilities,
} from '@/components/Integrations/capabilities';
import type { AdapterConfigField } from '@/components/Integrations/types';
import { errorMessage } from '../format';
import classes from './Integrations.module.css';

type Values = Record<string, unknown>;

function toInputValue(field: AdapterConfigField, raw: unknown): unknown {
  if (field.type === 'json') {
    if (raw === undefined || raw === null || raw === '') return '';
    return typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
  }
  if (field.type === 'email-list' || field.type === 'array-string') {
    return Array.isArray(raw) ? raw : [];
  }
  if (field.type === 'checkbox') return Boolean(raw);
  if (field.type === 'select' && field.required && (raw === undefined || raw === null)) {
    return field.options?.[0]?.value ?? '';
  }
  return raw ?? '';
}

function coerce(field: AdapterConfigField, raw: unknown): unknown {
  if (raw === undefined || raw === null) return undefined;
  switch (field.type) {
    case 'json': {
      if (typeof raw !== 'string') return raw;
      const trimmed = raw.trim();
      if (!trimmed) return undefined;
      try {
        return JSON.parse(trimmed);
      } catch {
        return raw;
      }
    }
    case 'number': {
      if (raw === '') return undefined;
      const n = typeof raw === 'number' ? raw : Number(raw);
      return Number.isFinite(n) ? n : undefined;
    }
    case 'checkbox':
      return Boolean(raw);
    case 'email-list':
    case 'array-string':
      return Array.isArray(raw) ? raw : [];
    default:
      return typeof raw === 'string' && raw.trim() === '' ? undefined : raw;
  }
}

function buildConfig(fields: AdapterConfigField[], values: Values): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const v = coerce(f, values[f.key]);
    if (v !== undefined) out[f.key] = v;
  }
  return out;
}

const stable = (o: Record<string, unknown>) =>
  JSON.stringify(
    Object.keys(o)
      .sort()
      .map((k) => [k, o[k]])
  );

const isEmpty = (v: unknown) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

export interface IntegrationEditorProps {
  kind: string;
  mode: 'create' | 'edit';
  initial?: { title: string; config: Record<string, unknown> };
  onCreate?: (body: NewIntegrationBody) => Promise<void>;
  onUpdate?: (body: UpdateIntegrationBody) => Promise<void>;
  onBack: () => void;
  /** Submit label in create mode; the form page's panel reads "Create and link to this form". */
  createLabel?: string;
}

export function IntegrationEditor({
  kind,
  mode,
  initial,
  onCreate,
  onUpdate,
  onBack,
  createLabel,
}: IntegrationEditorProps) {
  const { t } = useTranslation();
  const meta = getAdapterMeta(kind);
  const caps = useIntegrationCapabilities();
  const configFields = (meta?.configFields ?? []).filter((f) => isFieldShown(kind, f));
  const secretFields = (meta?.secretsFields ?? []).filter((f) => isFieldShown(kind, f));
  const editing = mode === 'edit';

  const [title, setTitle] = useState(initial?.title ?? '');
  const [config, setConfig] = useState<Values>(() => {
    const out: Values = {};
    for (const f of configFields) out[f.key] = toInputValue(f, initial?.config?.[f.key]);
    return out;
  });
  const [initialConfig] = useState(() => stable(buildConfig(configFields, config)));
  const [secrets, setSecrets] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!meta) {
    return <p className={classes.error}>{t('integrations.editor.unknownKind', { kind })}</p>;
  }

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = t('integrations.editor.nameRequired');
    const check = (group: 'config' | 'secrets', f: AdapterConfigField, v: unknown) => {
      const optional = !f.required || (group === 'secrets' && editing);
      if (isEmpty(v)) {
        if (!optional) next[`${group}.${f.key}`] = t('integrations.editor.required');
        return;
      }
      const msg = f.validate?.(f.type === 'json' ? v : coerce(f, v));
      if (msg) next[`${group}.${f.key}`] = validationMessage(msg, t);
    };
    for (const f of configFields) check('config', f, config[f.key]);
    for (const f of secretFields) check('secrets', f, secrets[f.key]);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError(null);
    if (!validate()) return;
    const builtConfig = buildConfig(configFields, config);
    const builtSecrets = buildConfig(secretFields, secrets);
    const hasSecrets = Object.keys(builtSecrets).length > 0;
    setBusy(true);
    try {
      if (editing) {
        const body: UpdateIntegrationBody = {};
        if (title.trim() !== initial?.title) body.title = title.trim();
        if (stable(builtConfig) !== initialConfig) body.config = builtConfig;
        if (hasSecrets) body.secrets = builtSecrets;
        await onUpdate?.(body);
      } else {
        await onCreate?.({
          title: title.trim(),
          kind,
          config: builtConfig,
          secrets: hasSecrets ? builtSecrets : undefined,
        });
      }
    } catch (err) {
      setServerError(errorMessage(err) ?? t('integrations.editor.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const renderField = (group: 'config' | 'secrets', f: AdapterConfigField) => {
    const values = group === 'config' ? config : secrets;
    const setValues = group === 'config' ? setConfig : setSecrets;
    const errorKey = `${group}.${f.key}`;
    const set = (v: unknown) => {
      setValues((prev) => ({ ...prev, [f.key]: v }));
      if (errors[errorKey]) {
        setErrors((prev) => {
          const { [errorKey]: _, ...rest } = prev;
          return rest;
        });
      }
    };
    const copy = adapterFieldCopy(kind, f, t);
    const keepSecret = group === 'secrets' && editing;
    const common = {
      label: copy.label,
      description: keepSecret ? t('integrations.editor.secretUnchanged') : copy.help,
      placeholder: keepSecret ? t('integrations.editor.secretPlaceholder') : copy.placeholder,
      required: f.required && !keepSecret,
      disabled: busy,
      error: errors[errorKey],
    };
    const value = values[f.key];
    const mono = /url|_id|domain/i.test(f.key) ? classes.monoInput : undefined;

    switch (f.type) {
      // A text input, so browsers do not offer to save the API key as an account password.
      case 'password':
        return (
          <TextInput
            key={f.key}
            {...common}
            classNames={{ input: classes.monoInput }}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => set(e.currentTarget.value)}
            autoComplete="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
          />
        );
      case 'select':
        return (
          <Select
            key={f.key}
            {...common}
            data={(f.options ?? []).map((o) => ({ value: o.value, label: t(o.label) }))}
            value={typeof value === 'string' ? value : null}
            onChange={(v) => set(v ?? '')}
          />
        );
      case 'checkbox':
        return (
          <Checkbox
            key={f.key}
            label={copy.label}
            description={copy.help}
            disabled={busy}
            checked={Boolean(value)}
            onChange={(e) => set(e.currentTarget.checked)}
          />
        );
      case 'number':
        return (
          <NumberInput
            key={f.key}
            {...common}
            value={typeof value === 'number' || typeof value === 'string' ? value : ''}
            onChange={(v) => set(v)}
            allowDecimal={false}
          />
        );
      case 'email-list':
      case 'array-string':
        return (
          <TagsInput
            key={f.key}
            {...common}
            value={Array.isArray(value) ? (value as string[]) : []}
            onChange={(v) => set(v)}
            splitChars={[',', ' ']}
            clearable
          />
        );
      case 'json':
        return (
          <Textarea
            key={f.key}
            {...common}
            classNames={{ input: classes.monoInput }}
            autosize
            minRows={3}
            maxRows={10}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => set(e.currentTarget.value)}
          />
        );
      default:
        return (
          <TextInput
            key={f.key}
            {...common}
            classNames={mono ? { input: mono } : undefined}
            value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
            onChange={(e) => set(e.currentTarget.value)}
          />
        );
    }
  };

  const name = adapterName(kind, t);

  return (
    <form className={classes.editor} onSubmit={submit} noValidate>
      <TextInput
        label={t('integrations.editor.name')}
        description={t('integrations.editor.nameHelp')}
        placeholder={adapterTitleHint(kind, t)}
        required
        value={title}
        onChange={(e) => setTitle(e.currentTarget.value)}
        error={errors.title}
        disabled={busy}
      />
      {configFields.map((f) => renderField('config', f))}
      {secretFields.map((f) => renderField('secrets', f))}

      <div className={classes.facts}>
        <strong>{t('integrations.editor.howItWorks', { name })}</strong>
        <span className={classes.help}>{deliveryFacts(capabilitiesFor(caps, kind), t)}</span>
      </div>

      {serverError && (
        <p className={classes.error} role="alert">
          {serverError}
        </p>
      )}

      <div className={classes.buttons}>
        <Button type="submit" loading={busy}>
          {editing
            ? t('integrations.editor.save')
            : (createLabel ?? t('integrations.editor.create'))}
        </Button>
        <Button variant="default" onClick={onBack} disabled={busy}>
          {editing ? t('ui.cancel') : t('integrations.editor.back')}
        </Button>
      </div>
    </form>
  );
}
